// The mobile localization boundary: every mobile copy string lives here
// so no mobile component hardcodes a literal in JSX.

export const mobileStrings = {
  tabs: {
    home: "Home",
    activity: "Activity",
    you: "You",
  },
  tabBadge: (label: string, count: number) =>
    `${label}, ${count > 99 ? "more than 99" : count} unread`,
  tabNeedsAction: (label: string) => `${label}, action needed`,
  nav: {
    tablistLabel: "Sections",
    back: "Back",
  },
  shell: {
    connecting: "Connecting…",
    syncing: "Syncing your messages…",
  },
  connection: {
    connecting: "Connecting…",
    offline: "Offline",
  },
  home: {
    title: "Home",
    wordmark: "Nurion",
    search: "Search",
    compose: "New conversation",
    filterAll: "All",
    allOrgs: "All organisations",
    switchOrg: (current: string) => `Organisation: ${current}. Switch organisation`,
    orgSheetTitle: "Organisations",
    closeOrgMenu: "Close organisations",
    orgUnread: (count: number) => `${count} unread`,
    roomsLabel: "Conversations",
    empty: "No conversations yet.",
    errorTitle: "Couldn't load your conversations.",
    errorRetry: "Try again",
    archiveError: (roomName: string) => `Couldn't archive ${roomName}.`,
    unreadOne: "1 unread",
    unreadOther: (count: number) => `${count} unread`,
    mentionsOne: "1 mention",
    mentionsOther: (count: number) => `${count} mentions`,
    sectionDirect: "Direct messages",
    sectionChannels: "Channels",
    sectionHidden: (count: number) => `Hidden · ${count}`,
    invitations: "Invitations",
    invitedBy: (who: string) => `Invited by ${who}`,
    accept: "Accept",
    decline: "Decline",
    arrangeLabel: (section: string) => `Arrange ${section}`,
    moveUp: "Move up",
    moveDown: "Move down",
  },
  sheet: {
    actionsLabel: (roomName: string) => `Actions for ${roomName}`,
    mute: "Mute",
    unmute: "Unmute",
    markRead: "Mark read",
    hide: "Hide",
    show: "Show in list",
    archive: "Archive",
    close: "Close",
  },
  activity: {
    title: "Activity",
    mentions: "Mentions",
    unread: "Unread",
    filtersLabel: "Filter activity",
    filterAll: "All",
    threads: "Threads",
    repliedIn: (name: string) => `${name} replied in thread`,
    markAllRead: "Mark everything read",
    empty: "Nothing needs you. Enjoy it.",
    reactedWith: (emoji: string) => `Reacted with ${emoji}`,
    unreadMessagesOne: "1 message",
    unreadMessagesOther: (count: number) => `${count} messages`,
  },
  you: {
    title: "You",
    empty: "Your profile and settings live here.",
    setStatus: "Set a status",
    photo: {
      add: "Add profile photo",
      change: "Change profile photo",
      choose: "Choose photo",
      remove: "Remove photo",
      failed: "Couldn’t update your photo. Try again.",
    },
    unlock: "Unlock your messages",
    encryption: "Encryption and devices",
    appearance: "Appearance",
    theme: { system: "System", light: "Light", dark: "Dark" },
    privacy: "Privacy policy",
    terms: "Terms",
    deleteAccount: "Delete account",
    blockedPeople: "Blocked people",
    deleteSheet: {
      title: "Delete account",
      body: "Continue to your Nurion account in the browser to review deletion and confirm your email address. Opening the page does not delete anything. You may need to transfer organisation ownership or resolve subscriptions first.",
      label: (username: string) => `Type ${username} to confirm`,
      cancel: "Keep account",
      confirm: "Continue in browser",
      pending: "Opening account page…",
      error: "Could not open your account page. Try again.",
      openedNote:
        "Your account page opens in another tab. If it did not open, use Continue in browser again.",
      afterDelete:
        "After deleting your account, sign out of Chat on this device to clear its local session.",
    },
    version: (version: string) => `Version ${version}`,
    signOut: "Sign out",
    signOutTitle: "Sign out?",
    signOutBody:
      "To read encrypted messages here again, you’ll need another signed-in device or your recovery key.",
    cancelSignOut: "Cancel",
  },
  split: {
    empty: "Pick a conversation.",
    paneLabel: "Conversation",
  },
  thread: {
    title: "Thread",
    empty: "Threads open here.",
    noReplies: "No replies yet",
    loading: "Loading replies",
    oneReply: "1 reply",
    replies: (count: number) => `${count} replies`,
    earlierReplies: (count: number) => `${count} earlier replies`,
    inRoom: (room: string) => `in ${room}`,
    dialogLabel: (room: string) => `Thread in ${room}`,
    composerPlaceholder: (sender: string) => `Reply to ${sender}…`,
    photo: "Photo",
    video: "Video",
    audio: "Audio",
    loadEarlier: "Load earlier replies",
    loadingEarlier: "Loading earlier replies…",
    earlierError: "Could not load earlier replies.",
    retry: "Retry",
  },
  room: {
    members: (count: number) => `${count} ${count === 1 ? "member" : "members"}`,
    roomOptions: "Room options",
    composerPlaceholder: (roomName: string) => `Message ${roomName}…`,
    preparingEncrypted: "Preparing encrypted room",
    preparingEncryptedBody: "Unlocking this room on this device. Other rooms stay available.",
  },
  messageSheet: {
    label: "Message actions",
    react: "Add reaction",
    moreReactions: "More reactions",
    copyText: "Copy text",
    save: "Save",
    quote: "Quote",
    deleteTitle: "Delete this message?",
    deleteBody: "It is removed for everyone in the room.",
    deleteFailed: "Couldn’t delete it. Check your connection and try again.",
    report: "Report message",
    edit: "Edit",
    delete: "Delete",
  },
  attach: {
    label: "Attach",
    camera: "Camera",
    photos: "Photos",
    files: "Files",
  },
  roomSheet: {
    notifications: "Notifications",
    members: "Members",
    archive: "Archive",
    leave: "Leave",
    block: (name: string) => `Block ${name}`,
    leaveError: (roomName: string) => `Couldn't leave ${roomName}.`,
  },
  newConversation: {
    title: "New conversation",
    searchLabel: "Search people",
    searchPlaceholder: "Name or @user:server",
    knownPeopleLabel: "People you know",
    resultsLabel: "Search results",
    searching: "Searching…",
    noKnownPeople: "No people to start with yet.",
    noResults: (term: string) => `No people match “${term}”.`,
    invalidId: "Enter a name, or an ID like @alice:nurion.",
    messageId: (id: string) => `Message ${id}`,
    error: "Couldn't open that conversation.",
  },
} as const;

/** Default quick-reaction emoji shown in the long-press sheet, in the
 *  order the Room ASCII spec lists them. */
export const QUICK_REACTIONS: readonly string[] = ["👍", "❤️", "😂", "✅", "🚀", "🎉", "👀"];
